import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { NavItem } from '../../config/navigation';
import { groupNavigation, type NavGroup } from '../../config/navigationGroups';

const STORAGE_KEY = 'helix_sidebar_open_sections';

/** Sections the user has opened; everything else starts closed. */
function readOpened(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function writeOpened(keys: string[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(keys)); } catch { /* private window: forget it */ }
}

interface GroupedNavProps {
  items: NavItem[];
  isActive: (href: string) => boolean;
  /** Icon-only sidebar: sections are dropped and every item renders as it would ungrouped. */
  collapsed: boolean;
  /** A top-level link (Dashboard, anything no section claims). */
  renderTopItem: (item: NavItem) => ReactNode;
  /** A link inside a section — the compact, indented style. */
  renderChildItem: (item: NavItem) => ReactNode;
}

function SectionHeader({ group, open, current, onToggle }: {
  group: NavGroup; open: boolean; current: boolean; onToggle: () => void;
}) {
  const Icon = group.def.icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      title={group.def.hint}
      data-testid={`nav-group-toggle-${group.def.key}`}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm font-semibold transition-colors',
        current ? 'text-primary' : 'text-foreground hover:bg-accent',
      )}
    >
      <span className={cn(
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md',
        current ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
      )}>
        <Icon className="h-4 w-4" />
      </span>
      {/* Section names are short enough to sit on one line and are never cut off. */}
      <span className="flex-1 whitespace-nowrap">{group.def.name}</span>
      <ChevronDown className={cn('h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform', open ? 'rotate-180' : '')} />
    </button>
  );
}

/**
 * The customer sidebar in sections: Dashboard pinned on top, then four
 * headings, closed by default. The section holding the current page opens by
 * itself so you can see where you are (and can still be folded); any section
 * you open yourself stays open, remembered per browser. Items sit on an
 * indented rail under their heading so what belongs where is visible at a glance.
 */
export default function GroupedNav({ items, isActive, collapsed, renderTopItem, renderChildItem }: GroupedNavProps) {
  const grouped = useMemo(() => groupNavigation(items), [items]);
  const activeKey = grouped.groups.find((g) => g.items.some((item) => isActive(item.href)))?.def.key ?? null;
  const [opened, setOpened] = useState<string[]>(readOpened);
  // Folding the section you are in lasts until you move to another page.
  const [foldedActive, setFoldedActive] = useState(false);

  useEffect(() => { writeOpened(opened); }, [opened]);
  useEffect(() => { setFoldedActive(false); }, [activeKey]);

  const isOpen = (key: string) => opened.includes(key) || (key === activeKey && !foldedActive);

  const toggle = (key: string) => {
    if (isOpen(key)) {
      setOpened((prev) => prev.filter((k) => k !== key));
      if (key === activeKey) setFoldedActive(true);
    } else {
      setOpened((prev) => [...prev, key]);
      if (key === activeKey) setFoldedActive(false);
    }
  };

  if (collapsed) {
    return <>{items.map((item) => renderTopItem(item))}</>;
  }

  return (
    <div className="space-y-1" data-testid="grouped-nav">
      {grouped.pinned.map((item) => renderTopItem(item))}
      {grouped.groups.map((group) => {
        const open = isOpen(group.def.key);
        return (
          <div key={group.def.key} className="border-t border-border/70 pt-2" data-testid={`nav-group-${group.def.key}`}>
            <SectionHeader
              group={group}
              open={open}
              current={activeKey === group.def.key}
              onToggle={() => toggle(group.def.key)}
            />
            {open && (
              <div className="mb-1 ml-[1.375rem] mt-1 space-y-0.5 border-l-2 border-border pl-2">
                {group.items.map((item) => renderChildItem(item))}
              </div>
            )}
          </div>
        );
      })}
      {grouped.rest.length > 0 && (
        <div className="space-y-1 border-t border-border/70 pt-2">
          {grouped.rest.map((item) => renderTopItem(item))}
        </div>
      )}
    </div>
  );
}
